// Web MIDI + MIDI Learn. Maps hardware CC knobs/faders to the panel's continuous controls.
// Runtime-only glue (requestMIDIAccess); the message decode + binding/learn state machine is pure
// (handleMessage) so it can be unit-tested without a device. Bindings persist in localStorage.

const STORE_KEY = 'diatom.midi.v1';
const CC_STATUS = 0xb0; // Control Change (high nibble); low nibble = channel

export type MidiControlId = string;

export interface MidiHandlers {
  /** A bound CC moved: deliver its normalized 0..1 value to the control. */
  onValue?: (id: MidiControlId, value01: number) => void;
  /** A learn completed: control `id` is now bound to `cc`. */
  onLearned?: (id: MidiControlId, cc: number) => void;
}

export class MidiController {
  private access: MIDIAccess | null = null;
  private bindings = new Map<MidiControlId, number>(); // control id -> CC number (0..127)
  private learning: MidiControlId | null = null;
  private handlers: MidiHandlers = {};

  constructor() {
    this.load();
  }

  static supported(): boolean {
    return typeof navigator !== 'undefined' && typeof navigator.requestMIDIAccess === 'function';
  }

  setHandlers(h: MidiHandlers): void {
    this.handlers = h;
  }

  /** Request access + attach to all inputs. Call from a user gesture (may prompt). */
  async enable(): Promise<boolean> {
    if (!MidiController.supported()) return false;
    try {
      this.access = await navigator.requestMIDIAccess({ sysex: false });
      this.attach();
      this.access.onstatechange = () => this.attach(); // pick up hot-plugged controllers
      return true;
    } catch {
      return false;
    }
  }

  private attach(): void {
    if (!this.access) return;
    for (const input of this.access.inputs.values()) {
      input.onmidimessage = (e: MIDIMessageEvent) => {
        if (e.data) this.handleMessage(e.data);
      };
    }
  }

  /**
   * Pure message handler (also the unit-test entry point). Decodes a Control Change; in learn mode
   * binds the pending control to the incoming CC, otherwise drives every control bound to that CC.
   */
  handleMessage(data: Uint8Array | number[]): void {
    if (!data || data.length < 3) return;
    if ((data[0] & 0xf0) !== CC_STATUS) return; // only Control Change for now
    const cc = data[1];
    const value01 = Math.max(0, Math.min(1, data[2] / 127));
    if (this.learning !== null) {
      const id = this.learning;
      this.learning = null;
      this.bind(id, cc);
      this.handlers.onLearned?.(id, cc);
      this.handlers.onValue?.(id, value01); // apply the move that taught it
      return;
    }
    for (const [id, boundCc] of this.bindings) {
      if (boundCc === cc) this.handlers.onValue?.(id, value01);
    }
  }

  /** Arm a control to bind to the next CC that moves. */
  learn(id: MidiControlId): void {
    this.learning = id;
  }
  cancelLearn(): void {
    this.learning = null;
  }
  isLearning(id?: MidiControlId): boolean {
    return id === undefined ? this.learning !== null : this.learning === id;
  }

  bind(id: MidiControlId, cc: number): void {
    this.bindings.set(id, cc);
    this.persist();
  }
  unbind(id: MidiControlId): void {
    this.bindings.delete(id);
    this.persist();
  }
  bindingFor(id: MidiControlId): number | undefined {
    return this.bindings.get(id);
  }
  allBindings(): Array<[MidiControlId, number]> {
    return [...this.bindings];
  }

  private persist(): void {
    try {
      localStorage.setItem(STORE_KEY, JSON.stringify([...this.bindings]));
    } catch {
      /* storage unavailable */
    }
  }
  private load(): void {
    try {
      const raw = localStorage.getItem(STORE_KEY);
      const arr = raw ? JSON.parse(raw) : [];
      if (Array.isArray(arr)) {
        this.bindings = new Map(
          arr.filter((e) => Array.isArray(e) && typeof e[0] === 'string' && typeof e[1] === 'number'),
        );
      }
    } catch {
      /* ignore bad storage */
    }
  }
}
